/**
 * useFilePasteCapture Hook
 *
 * Headless, UI-free clipboard-paste zone — the paste counterpart of
 * `useFileDropZone` (`@/lib/hooks`). A paste event only fires on the focused
 * element, so unlike a drop zone this cannot be spread onto a container: it
 * listens on the document for the duration of the consumer's mount and forwards
 * the clipboard payload for pastes that landed on ordinary, non-editable page
 * ground.
 *
 * It lives in the chat arche rather than `@/lib/hooks` because chat is its only
 * consumer; promote it beside `useFileDropZone` when a second surface wants it.
 *
 * Three guards keep it from stealing a paste meant for something else:
 *
 * - **Already handled.** A paste an editor consumed (a rich-text composer that
 *   attached the files itself) arrives here with `defaultPrevented` set, and is
 *   skipped — otherwise a pasted screenshot would attach twice.
 * - **Editable target.** A paste into an input, textarea, or contenteditable
 *   belongs to that field. Pasting an image into a search box searches for its
 *   text; it does not attach a file somewhere else on the page.
 * - **Inside a modal.** A dialog's focus trap parks focus on whatever it opened
 *   with — often a plain button, which is not editable — so without this the
 *   page behind an open dialog would quietly swallow the paste and yank focus
 *   out of the trap.
 *
 * The consumer decides what — if anything — the clipboard payload means to it,
 * which is what keeps this hook free of any one surface's file policy.
 *
 * Example:
 *   useFilePasteCapture({
 *     onPaste: (data) => addFiles(clipboardAttachmentFiles(data)),
 *     disabled: !activeChannelId,
 *   });
 */

import { useEffect, useRef } from 'react';

export interface UseFilePasteCaptureOptions {
  /** Called with the clipboard payload of an unhandled, non-editable paste. */
  onPaste: (clipboardData: DataTransfer) => void;
  /** When true, no listener is attached and pastes are ignored. */
  disabled?: boolean;
}

/** Whether something nearer the paste than this page owns the clipboard payload. */
function pasteBelongsElsewhere(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // A field that handles its own clipboard.
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // A paste inside a dialog belongs to that dialog, not the page behind it.
  return target.closest('[role="dialog"], [aria-modal="true"]') !== null;
}

export function useFilePasteCapture({ onPaste, disabled = false }: UseFilePasteCaptureOptions) {
  // Held in a ref so a consumer's inline callback doesn't detach and re-attach
  // the listener on every render.
  const onPasteRef = useRef(onPaste);
  useEffect(() => {
    onPasteRef.current = onPaste;
  }, [onPaste]);

  useEffect(() => {
    if (disabled) return;
    const handlePaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented) return;
      if (pasteBelongsElsewhere(event.target)) return;
      if (!event.clipboardData) return;
      onPasteRef.current(event.clipboardData);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [disabled]);
}

export default useFilePasteCapture;
